import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileDown, Loader2, BarChart3, FileText, TrendingUp } from "lucide-react";
import api from "@/services/api";
import { deduplicateAnalysesByDepot } from "@/lib/analysis-dedup";
import { useToast } from "@/hooks/use-toast";
import { useAOPdfExport } from "@/hooks/useAOPdfExport";
import { useRankingPdfExport } from "@/hooks/useRankingPdfExport";
import { useGlobalComparisonPdfExport } from "@/hooks/useGlobalComparisonPdfExport";

interface Evaluation {
  id: string;
  nom: string;
  description?: string;
  ponderation_globale_pct: number;
}

interface CritereEvaluation {
  id: string;
  nom: string;
  description?: string;
  ponderation_pct: number;
  contenu_html?: string;
}

interface Stats {
  totalCV: number;
  totalAnalyses: number;
  shortlist: number;
  consider: number;
  reject: number;
}

interface Service {
  id: string;
  nom: string;
}

interface SocieteNotePdf {
  societe_nom: string;
  note_qualite_offre: number | null;
  note_prix: number | null;
  note_durabilite: number | null;
  note_references: number | null;
  poids_qualite_offre: number | null;
  poids_prix: number | null;
  poids_durabilite: number | null;
  poids_references: number | null;
  score_pondere: number | null;
  commentaire: string | null;
}

interface AppelOffre {
  id: string;
  titre: string;
  reference: string;
  description?: string;
  date_limite: string;
  date_publication?: string;
  date_debut_redaction_ao?: string;
  date_qr_soumissionnaires?: string;
  date_reponses_qr?: string;
  date_analyse_offres?: string;
  date_debut_soutenance?: string;
  date_fin_soutenance?: string;
  date_adjudication?: string;
  date_contractualisation?: string;
  interlocuteur_da?: string;
  ao_manager?: string;
  lien_ao?: string;
  statut?: string;
  competences_html?: string | null;
  certifications_html?: string | null;
  poids_competences_pct?: number;
  poids_certifications_pct?: number;
  seuil_shortlist?: number;
  seuil_consider?: number;
}

interface CandidatDetail {
  id: string;
  nom: string;
  score: number;
  decision: string;
  created_at: string;
  model_used?: string;
  scores_criteres?: { [key: string]: number };
}

interface SocieteRanking {
  societe_id: string;
  societe_nom: string;
  nb_candidatures: number;
  score_moyen: number;
  decision_counts: {
    shortlist: number;
    consider: number;
    reject: number;
  };
  candidats: CandidatDetail[];
  scores_criteres: { [key: string]: { score: number; count: number } };
}

interface AOReportingTabProps {
  appelOffre: AppelOffre;
  evaluations: Evaluation[];
  criteresMap: Record<string, CritereEvaluation[]>;
  stats: Stats;
  services: Service[];
  societeNotes: SocieteNotePdf[];
}

export const AOReportingTab: React.FC<AOReportingTabProps> = ({
  appelOffre,
  evaluations,
  criteresMap,
  stats,
  services,
  societeNotes,
}) => {
  const { toast } = useToast();
  const { exportAOToPdf } = useAOPdfExport();
  const { exportRankingToPdf } = useRankingPdfExport();
  const { exportGlobalComparisonToPdf } = useGlobalComparisonPdfExport();

  const [rankings, setRankings] = useState<SocieteRanking[]>([]);
  const [rankingsLoading, setRankingsLoading] = useState(false);
  const [exportingAO, setExportingAO] = useState(false);
  const [exportingRanking, setExportingRanking] = useState(false);
  const [exportingComparison, setExportingComparison] = useState(false);

  // Load ranking data (analyses grouped by societe)
  useEffect(() => {
    loadRankings();
  }, [appelOffre.id]);

  const loadRankings = async () => {
    setRankingsLoading(true);
    try {
      const { data: response } = await api.get(`/analyses`, { params: { aoId: appelOffre.id } });
      const analysesData = response?.data?.analyses || [];

      // Deduplicate: keep only the latest analysis per depot (candidat)
      const dedupedAnalyses = deduplicateAnalysesByDepot(
        analysesData.map((item: any) => ({ ...item, depotId: item.depotId || item.depot_id || item.depot?.id }))
      );

      // Group analyses by societe
      const societeMap = new Map<string, SocieteRanking>();

      dedupedAnalyses.forEach((item: any) => {
        const societeId = item.depot?.candidat?.societe?.id || item.depot?.candidat?.societeId;
        const societeNom = item.depot?.candidat?.societe?.nom || 'Inconnue';
        if (!societeId) return;

        if (!societeMap.has(societeId)) {
          societeMap.set(societeId, {
            societe_id: societeId,
            societe_nom: societeNom,
            nb_candidatures: 0,
            score_moyen: 0,
            decision_counts: { shortlist: 0, consider: 0, reject: 0 },
            candidats: [],
            scores_criteres: {},
          });
        }

        const societe = societeMap.get(societeId)!;
        societe.nb_candidatures++;

        const score = item.overallScore ?? item.overall_score ?? 0;
        const decision = item.decision || 'reject';

        if (decision === 'shortlist') societe.decision_counts.shortlist++;
        else if (decision === 'consider') societe.decision_counts.consider++;
        else societe.decision_counts.reject++;

        societe.candidats.push({
          id: item.id,
          nom: item.depot?.candidat?.nom || 'Inconnu',
          score,
          decision,
          created_at: item.createdAt || item.created_at || '',
          model_used: item.modelUsed || item.model_used,
          scores_criteres: item.resultatJson?.criteria_scores || item.resultat_json?.criteria_scores,
        });

        // Aggregate criteria scores
        const criteriaScores = item.resultatJson?.criteria_scores || item.resultat_json?.criteria_scores || {};
        Object.entries(criteriaScores).forEach(([key, val]: [string, any]) => {
          const criteriaScore = typeof val === 'number' ? val : val?.score ?? 0;
          if (!societe.scores_criteres[key]) {
            societe.scores_criteres[key] = { score: 0, count: 0 };
          }
          societe.scores_criteres[key].score += criteriaScore;
          societe.scores_criteres[key].count++;
        });
      });

      // Calculate averages
      societeMap.forEach(societe => {
        if (societe.nb_candidatures > 0) {
          societe.score_moyen = societe.candidats.reduce((sum, c) => sum + c.score, 0) / societe.nb_candidatures;
        }
        Object.values(societe.scores_criteres).forEach(criteria => {
          if (criteria.count > 0) {
            criteria.score = criteria.score / criteria.count;
          }
        });
      });

      // Sort by score descending
      const sorted = Array.from(societeMap.values()).sort((a, b) => b.score_moyen - a.score_moyen);
      setRankings(sorted);
    } catch (error) {
      console.error('Erreur lors du chargement des rankings:', error);
    } finally {
      setRankingsLoading(false);
    }
  };

  // Build global societe data for comparison PDF
  const globalSocietesData = useMemo(() => {
    return societeNotes
      .filter(n => n.score_pondere !== null)
      .map(n => ({
        societe_id: '', // Not available from SocieteNotePdf but not critical for PDF
        societe_nom: n.societe_nom,
        note_qualite_offre: n.note_qualite_offre,
        note_prix: n.note_prix,
        note_durabilite: n.note_durabilite,
        note_references: n.note_references,
        poids_qualite_offre: n.poids_qualite_offre ?? 25,
        poids_prix: n.poids_prix ?? 25,
        poids_durabilite: n.poids_durabilite ?? 25,
        poids_references: n.poids_references ?? 25,
        score_pondere: n.score_pondere ?? 0,
      }));
  }, [societeNotes]);

  // Build selectedCandidatesData for comparison PDF (IA quality column)
  const selectedCandidatesData = useMemo(() => {
    return rankings.map(r => ({
      societe_id: r.societe_id,
      societe_nom: r.societe_nom,
      nb_candidatures: r.nb_candidatures,
      score_moyen: r.score_moyen,
      candidats: r.candidats.map(c => ({
        id: c.id,
        nom: c.nom,
        score: c.score,
        decision: c.decision,
        created_at: c.created_at,
        model_used: c.model_used,
        scores_criteres: c.scores_criteres,
      })),
    }));
  }, [rankings]);

  const handleExportAO = async () => {
    setExportingAO(true);
    try {
      await exportAOToPdf(appelOffre, evaluations, criteresMap, stats, services, societeNotes);
    } finally {
      setExportingAO(false);
    }
  };

  const handleExportRanking = async () => {
    if (rankings.length === 0) {
      toast({
        title: "Aucune donnee",
        description: "Aucun ranking disponible pour l'export",
        variant: "destructive",
      });
      return;
    }
    setExportingRanking(true);
    try {
      await exportRankingToPdf(
        { id: appelOffre.id, titre: appelOffre.titre, reference: appelOffre.reference },
        rankings,
      );
    } finally {
      setExportingRanking(false);
    }
  };

  const handleExportComparison = async () => {
    if (globalSocietesData.length === 0) {
      toast({
        title: "Aucune donnee",
        description: "Aucune note de societe disponible pour l'export",
        variant: "destructive",
      });
      return;
    }
    setExportingComparison(true);
    try {
      const allIds = globalSocietesData.map(s => s.societe_id);
      await exportGlobalComparisonToPdf(
        { id: appelOffre.id, titre: appelOffre.titre, reference: appelOffre.reference },
        globalSocietesData,
        allIds,
        selectedCandidatesData,
      );
    } finally {
      setExportingComparison(false);
    }
  };

  const totalCandidatures = rankings.reduce((sum, r) => sum + r.nb_candidatures, 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* AO Report Card */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <FileText className="h-5 w-5 mr-2 text-blue-500" />
              Rapport AO
            </CardTitle>
            <CardDescription>
              Fiche complete de l'appel d'offres avec planning, criteres et notes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Evaluations</span>
                <Badge variant="secondary">{evaluations.length}</Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Criteres</span>
                <Badge variant="secondary">
                  {Object.values(criteresMap).reduce((sum, arr) => sum + arr.length, 0)}
                </Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Notes societes</span>
                <Badge variant="secondary">{societeNotes.length}</Badge>
              </div>
              <Button
                className="w-full mt-4"
                onClick={handleExportAO}
                disabled={exportingAO}
              >
                {exportingAO ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileDown className="h-4 w-4 mr-2" />
                )}
                {exportingAO ? "Generation..." : "Telecharger PDF"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* CV Ranking Card */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <BarChart3 className="h-5 w-5 mr-2 text-purple-500" />
              Ranking CV
            </CardTitle>
            <CardDescription>
              Classement des societes par score d'analyse IA des CV
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Societes</span>
                <Badge variant="secondary">
                  {rankingsLoading ? "..." : rankings.length}
                </Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">CV analyses</span>
                <Badge variant="secondary">
                  {rankingsLoading ? "..." : totalCandidatures}
                </Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Analyses totales</span>
                <Badge variant="secondary">{stats.totalAnalyses}</Badge>
              </div>
              <Button
                className="w-full mt-4"
                onClick={handleExportRanking}
                disabled={exportingRanking || rankingsLoading || rankings.length === 0}
              >
                {exportingRanking ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileDown className="h-4 w-4 mr-2" />
                )}
                {exportingRanking ? "Generation..." : "Telecharger PDF"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Global Comparison Card */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <TrendingUp className="h-5 w-5 mr-2 text-green-500" />
              Comparaison Globale
            </CardTitle>
            <CardDescription>
              Vue comparative avec heatmap, radar et classement global
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Societes notees</span>
                <Badge variant="secondary">{globalSocietesData.length}</Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Criteres globaux</span>
                <Badge variant="secondary">4</Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Donnees IA</span>
                <Badge variant="secondary">
                  {rankings.filter(r => r.score_moyen > 0).length} societes
                </Badge>
              </div>
              <Button
                className="w-full mt-4"
                onClick={handleExportComparison}
                disabled={exportingComparison || globalSocietesData.length === 0}
              >
                {exportingComparison ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileDown className="h-4 w-4 mr-2" />
                )}
                {exportingComparison ? "Generation..." : "Telecharger PDF"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
